import React, { useMemo, useRef, useState, useLayoutEffect, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ZoomIn, ZoomOut, Maximize2, GitBranch, Flag, Play, AlertTriangle, PenSquare } from "lucide-react";

const NODE_W = 208;
const NODE_H = 92;
const COL_W = 250;
const ROW_H = 168;
const ACT_COLORS = ["#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6", "#14b8a6", "#f43f5e"];

// Layered top-to-bottom auto-layout of the branching story graph.
function layout(chapters, acts) {
  if (!chapters.length) return { nodes: [], edges: [] };
  const byId = Object.fromEntries(chapters.map((c) => [c.id, c]));
  const sorted = [...chapters].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const startId = sorted[0].id;

  // edges from valid choice targets
  const edges = [];
  const outCount = {};
  chapters.forEach((c) => {
    const ch = Array.isArray(c.choices) ? c.choices : [];
    ch.forEach((choice) => {
      if (choice.target_id && byId[choice.target_id]) {
        edges.push({ from: c.id, to: choice.target_id, label: choice.label || "" });
        outCount[c.id] = (outCount[c.id] || 0) + 1;
      }
    });
  });

  // BFS shortest-depth from start
  const depth = { [startId]: 0 };
  const adj = {};
  edges.forEach((e) => { (adj[e.from] = adj[e.from] || []).push(e.to); });
  const queue = [startId];
  while (queue.length) {
    const u = queue.shift();
    (adj[u] || []).forEach((v) => {
      if (depth[v] === undefined) { depth[v] = depth[u] + 1; queue.push(v); }
    });
  }

  const reachable = new Set(Object.keys(depth));
  const orphans = sorted.filter((c) => !reachable.has(c.id));
  const maxDepth = Object.values(depth).length ? Math.max(...Object.values(depth)) : 0;

  // group reachable by depth, ordered by chapter order for stability
  const levels = {};
  sorted.forEach((c) => {
    if (reachable.has(c.id)) {
      const d = depth[c.id];
      (levels[d] = levels[d] || []).push(c);
    }
  });

  const actColor = {};
  (acts || []).forEach((a, i) => { actColor[a.id] = ACT_COLORS[i % ACT_COLORS.length]; });

  const nodes = [];
  Object.keys(levels).map(Number).sort((a, b) => a - b).forEach((d) => {
    levels[d].forEach((c, i) => {
      nodes.push(makeNode(c, i * COL_W, d * ROW_H, actColor, startId, outCount, false));
    });
  });
  // orphans in their own lane at the bottom
  orphans.forEach((c, i) => {
    nodes.push(makeNode(c, i * COL_W, (maxDepth + 1.4) * ROW_H, actColor, startId, outCount, true));
  });

  return { nodes, edges };
}

function makeNode(c, x, y, actColor, startId, outCount, orphan) {
  return {
    id: c.id, title: c.title || "Untitled section", x, y,
    color: actColor[c.act_id] || "#64748b",
    isStart: c.id === startId,
    isEnding: !(outCount[c.id] > 0),
    choiceCount: outCount[c.id] || 0,
    orphan,
  };
}

export default function BranchMap({ chapters, acts, onOpenChapter }) {
  const wrapRef = useRef(null);
  const panRef = useRef(null);
  const movedRef = useRef(false);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [t, setT] = useState({ x: 0, y: 0, k: 1 });
  const [hover, setHover] = useState(null);

  const { nodes, edges } = useMemo(() => layout(chapters, acts), [chapters, acts]);
  const nodeById = useMemo(() => Object.fromEntries(nodes.map((n) => [n.id, n])), [nodes]);

  useLayoutEffect(() => {
    if (!wrapRef.current) return;
    const el = wrapRef.current;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fit = useCallback(() => {
    if (!nodes.length || !size.w) return;
    const pad = 70;
    const minX = Math.min(...nodes.map((n) => n.x));
    const maxX = Math.max(...nodes.map((n) => n.x + NODE_W));
    const minY = Math.min(...nodes.map((n) => n.y));
    const maxY = Math.max(...nodes.map((n) => n.y + NODE_H));
    const cw = Math.max(1, maxX - minX);
    const ch = Math.max(1, maxY - minY);
    const k = Math.max(0.25, Math.min((size.w - pad * 2) / cw, (size.h - pad * 2) / ch, 1.15));
    setT({ k, x: (size.w - cw * k) / 2 - minX * k, y: (size.h - ch * k) / 2 - minY * k });
  }, [nodes, size]);

  // fit on first layout / size ready
  useEffect(() => { fit(); }, [fit]);

  // non-passive wheel zoom
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      setT((prev) => {
        const factor = e.deltaY < 0 ? 1.12 : 0.89;
        const k = Math.max(0.25, Math.min(2.4, prev.k * factor));
        const ratio = k / prev.k;
        return { k, x: mx - (mx - prev.x) * ratio, y: my - (my - prev.y) * ratio };
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const onMouseDown = (e) => {
    panRef.current = { sx: e.clientX, sy: e.clientY, ox: t.x, oy: t.y };
    movedRef.current = false;
  };
  const onMouseMove = (e) => {
    if (!panRef.current) return;
    const dx = e.clientX - panRef.current.sx;
    const dy = e.clientY - panRef.current.sy;
    if (Math.abs(dx) + Math.abs(dy) > 4) movedRef.current = true;
    setT((prev) => ({ ...prev, x: panRef.current.ox + dx, y: panRef.current.oy + dy }));
  };
  const endPan = () => { panRef.current = null; };
  const zoom = (factor) => setT((p) => {
    const k = Math.max(0.25, Math.min(2.4, p.k * factor));
    const cx = size.w / 2, cy = size.h / 2, ratio = k / p.k;
    return { k, x: cx - (cx - p.x) * ratio, y: cy - (cy - p.y) * ratio };
  });

  const hasLinks = edges.length > 0;

  return (
    <div className="flex-1 relative overflow-hidden bg-background" data-testid="branch-map">
      {/* dotted grid */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.5]"
        style={{ backgroundImage: "radial-gradient(hsl(var(--border)) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />

      {/* toolbar */}
      <div className="absolute top-4 left-4 z-20 flex items-center gap-2">
        <div className="flex items-center gap-1.5 rounded-lg border border-border bg-card/90 backdrop-blur px-3 py-2 text-sm">
          <GitBranch className="w-4 h-4 text-primary" />
          <span className="font-semibold">Branch Map</span>
          <span className="text-muted-foreground">· {nodes.length} sections · {edges.length} links</span>
        </div>
      </div>
      <div className="absolute top-4 right-4 z-20 flex items-center gap-1.5">
        <Button variant="outline" size="icon" onClick={() => zoom(1.2)} data-testid="map-zoom-in" className="h-9 w-9 bg-card/90 backdrop-blur"><ZoomIn className="w-4 h-4" /></Button>
        <Button variant="outline" size="icon" onClick={() => zoom(0.83)} data-testid="map-zoom-out" className="h-9 w-9 bg-card/90 backdrop-blur"><ZoomOut className="w-4 h-4" /></Button>
        <Button variant="outline" size="sm" onClick={fit} data-testid="map-fit" className="h-9 gap-1.5 bg-card/90 backdrop-blur"><Maximize2 className="w-4 h-4" /> Fit</Button>
      </div>

      {/* legend */}
      <div className="absolute bottom-4 left-4 z-20 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card/90 backdrop-blur px-3 py-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><Play className="w-3.5 h-3.5 text-emerald-500" /> Start</span>
        <span className="flex items-center gap-1.5"><Flag className="w-3.5 h-3.5 text-amber-500" /> Ending</span>
        <span className="flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-rose-500" /> Orphan (nothing links here)</span>
      </div>

      {!hasLinks && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-6 pointer-events-none">
          <GitBranch className="w-12 h-12 text-muted-foreground/40 mb-4" />
          <p className="text-muted-foreground max-w-sm">
            No branches yet. Add choices to your sections (in the Manuscript or Blueprint) and they'll appear here as a map.
          </p>
        </div>
      )}

      <div ref={wrapRef} className="absolute inset-0 cursor-grab active:cursor-grabbing"
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={endPan} onMouseLeave={endPan}>
        <svg width="100%" height="100%" className="select-none">
          <defs>
            <marker id="bm-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" style={{ fill: "hsl(var(--muted-foreground))" }} />
            </marker>
          </defs>
          <g transform={`translate(${t.x},${t.y}) scale(${t.k})`}>
            {/* edges */}
            {edges.map((e, i) => {
              const a = nodeById[e.from], b = nodeById[e.to];
              if (!a || !b) return null;
              const sx = a.x + NODE_W / 2, sy = a.y + NODE_H;
              const tx = b.x + NODE_W / 2, ty = b.y;
              const active = hover && (hover === e.from || hover === e.to);
              const d = `M ${sx} ${sy} C ${sx} ${sy + 50}, ${tx} ${ty - 50}, ${tx} ${ty}`;
              return (
                <path key={i} d={d} fill="none" markerEnd="url(#bm-arrow)"
                  style={{
                    stroke: active ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))",
                    strokeOpacity: active ? 0.9 : (!hover ? 0.4 : 0.14),
                  }}
                  strokeWidth={active ? 2.5 : 1.6} />
              );
            })}

            {/* nodes */}
            {nodes.map((n) => (
              <foreignObject key={n.id} x={n.x} y={n.y} width={NODE_W} height={NODE_H} style={{ overflow: "visible" }}>
                <button
                  data-testid={`map-node-${n.id}`}
                  onMouseEnter={() => setHover(n.id)} onMouseLeave={() => setHover(null)}
                  onClick={() => { if (!movedRef.current) onOpenChapter(n.id); }}
                  className={`group w-full h-full text-left rounded-xl border bg-card p-3 shadow-sm transition-all hover:shadow-lg hover:-translate-y-0.5
                    ${n.orphan ? "border-rose-500/50" : hover === n.id ? "border-primary" : "border-border"}`}
                  style={{ borderLeft: `4px solid ${n.color}` }}>
                  <div className="flex items-center gap-1.5 mb-1">
                    {n.isStart && <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-400"><Play className="w-3 h-3" /> Start</span>}
                    {n.orphan && <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-rose-500"><AlertTriangle className="w-3 h-3" /> Orphan</span>}
                    {n.isEnding && !n.orphan && <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-amber-500"><Flag className="w-3 h-3" /> The End</span>}
                  </div>
                  <div className="font-display font-bold text-sm leading-tight line-clamp-2">{n.title}</div>
                  <div className="mt-auto pt-1.5 flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground">
                      {n.choiceCount > 0 ? `${n.choiceCount} choice${n.choiceCount !== 1 ? "s" : ""}` : "no choices"}
                    </span>
                    <span className="text-[10px] text-primary font-semibold inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <PenSquare className="w-3 h-3" /> Open
                    </span>
                  </div>
                </button>
              </foreignObject>
            ))}

            {/* edge labels (rendered above nodes so they stay readable) */}
            {edges.map((e, i) => {
              const a = nodeById[e.from], b = nodeById[e.to];
              if (!a || !b || !e.label) return null;
              const sx = a.x + NODE_W / 2, sy = a.y + NODE_H;
              const tx = b.x + NODE_W / 2, ty = b.y;
              const active = hover && (hover === e.from || hover === e.to);
              const lx = (sx + tx) / 2, ly = (sy + ty) / 2;
              return (
                <foreignObject key={`l${i}`} x={lx - 75} y={ly - 12} width="150" height="24"
                  style={{ overflow: "visible", opacity: !hover || active ? 1 : 0.2 }}>
                  <div className="flex justify-center">
                    <span className={`truncate max-w-[150px] text-[10px] leading-none px-2 py-1 rounded-full border bg-card ${active ? "border-primary/60 text-foreground" : "border-border text-muted-foreground"}`}
                      title={e.label}>{e.label}</span>
                  </div>
                </foreignObject>
              );
            })}
          </g>
        </svg>
      </div>
    </div>
  );
}
